import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/pages/Profile";

// Org-wide presentation directory (display name + avatar per user_id), fetched once and shared.
// Used to render avatar + nickname against completed tasks/records. It is presentation ONLY —
// the underlying records keep their authoritative user_id + real-name snapshot for audit.
const DirectoryContext = createContext({ users: {}, ready: false });

export function DirectoryProvider({ children }) {
  const [users, setUsers] = useState({});
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/profile/directory").then((r) => { if (alive) { setUsers(r.data.users || {}); setReady(true); } }).catch(() => setReady(true));
    return () => { alive = false; };
  }, []);
  return <DirectoryContext.Provider value={{ users, ready }}>{children}</DirectoryContext.Provider>;
}

export const useDirectory = () => useContext(DirectoryContext);

function initialsFrom(name) {
  return (name || "?").split(" ").map((s) => s[0]).filter(Boolean).join("").slice(0, 2).toUpperCase() || "?";
}

// Renders avatar + preferred display name for a completing user. Fallback order:
// avatar+nickname -> nickname -> real-name snapshot -> initials. `userId` is optional; when the
// record only carries a name snapshot, that snapshot is shown (audit identity is never lost).
export function TeamIdentity({ userId, fallbackName, size = 20, className = "", testid }) {
  const { users } = useDirectory();
  const entry = userId ? users[userId] : null;
  const name = entry?.display_name || fallbackName || "Unknown";
  const url = entry?.avatar_url;
  const px = `${size}px`;
  return (
    <span className={`inline-flex items-center gap-1.5 align-middle ${className}`} data-testid={testid}>
      <Avatar className="border border-slate-200" style={{ height: px, width: px }}>
        <AvatarImage src={avatarSrc(url)} alt={name} />
        <AvatarFallback className="bg-brand-light text-brand-dark font-bold" style={{ fontSize: `${Math.round(size * 0.42)}px` }}>{entry?.initials || initialsFrom(fallbackName)}</AvatarFallback>
      </Avatar>
      <span className="truncate">{name}</span>
    </span>
  );
}
